import { useState } from "react";
import { addToast, Button } from "@heroui/react";
import { UserCirclePlusIcon } from "@phosphor-icons/react";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { useShallow } from "zustand/shallow";

import googleLogo from "@/assets/images/google-logo.png";
import appleLogo from "@/assets/images/apple-logo.svg";
import { supabase } from "@/services/supabase/client";
import { useBoundStore } from "@/app/store";
import { DrawerAndModal } from "@/shared/components/common/DrawerAndModal";
import { saveAppleFullName } from "@/services/api/utils/auth";
import { DEEP_LINK_GOOGLE } from "@/routes/public/SignInRoute";
import { supabaseUrl } from "@/config/env";

interface ManageGuestSignInProps {
  id: string;
  externalState: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
  };
}

export function ManageGuestSignIn({ id, externalState }: ManageGuestSignInProps) {
  const [isLoading, setIsLoading] = useState<"google" | "apple" | null>(null);
  const { isNativeDevice, platform, userId } = useBoundStore(
    useShallow((state) => ({
      isNativeDevice: state.device.isNative,
      platform: state.device.platform,
      userId: state.user.id,
    }))
  );

  const handleGoogleSignIn = async () => {
    setIsLoading("google");
    try {
      const { error } = await supabase.auth.linkIdentity({
        provider: "google",
        options: {
          redirectTo: isNativeDevice ? DEEP_LINK_GOOGLE : undefined,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });
      if (error) throw error;
    } catch (error) {
      console.error("Google linking error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to link Google account";
      addToast({
        color: "danger",
        title: "Google linking failed",
        description: errorMessage.includes("identity_already_exists")
          ? "This Google account is already registered to another user."
          : errorMessage,
      });
    } finally {
      setIsLoading(null);
    }
  };

  const handleAppleSignIn = async () => {
    setIsLoading("apple");
    try {
      if (platform !== "ios") {
        const { error } = await supabase.auth.linkIdentity({
          provider: "apple",
          options: { redirectTo: `${supabaseUrl}/auth/v1/callback` },
        });
        if (error) throw error;
        return;
      }

      try {
        await SocialLogin.initialize({ apple: {} });
      } catch (error) {
        console.error("Failed to initialize SocialLogin:", error);
      }

      const result = await SocialLogin.login({
        provider: "apple",
        options: {
          scopes: ["email", "name"],
        },
      });

      if (result.result?.idToken) {
        const { error: supabaseError } = await supabase.auth.linkIdentity({
          provider: "apple",
          token: result.result.idToken,
        });

        if (supabaseError) throw supabaseError;

        if (result.result.profile && userId) {
          await saveAppleFullName(result.result.profile, userId);
        }

        addToast({
          color: "success",
          title: "Account linked",
          description: "Your Apple account has been successfully linked.",
        });
        externalState.onOpenChange(false);
      } else {
        throw new Error("No identityToken provided by Apple");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorCode = (error as { code?: string })?.code;
      const isUserCanceled =
        errorCode === "ERR_REQUEST_CANCELED" ||
        errorCode === "userCanceled" ||
        errorCode === "1" ||
        errorCode === "1001" ||
        errorMessage.toLowerCase().includes("canceled") ||
        errorMessage.includes("error 1001");

      if (isUserCanceled) {
        console.log("Apple sign-in canceled by user");
      } else {
        console.error("Apple linking error:", error);
        addToast({
          color: "danger",
          title: "Apple linking failed",
          description: errorMessage.includes("identity_already_exists")
            ? "This Apple account is already registered to another user."
            : errorMessage || "Failed to link Apple account",
        });
      }
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <DrawerAndModal
      id={id}
      icon={<UserCirclePlusIcon />}
      title="Create an Account or Sign In"
      closeLabel="Close"
      externalState={externalState}
    >
      <div className="flex flex-col gap-4 py-2">
        <p className="text-default-600 text-sm">
          Sign in to save and sync your notes, tags, and tasks across all your devices securely.
        </p>

        <div className="flex flex-col gap-3">
          <Button
            size="lg"
            fullWidth
            variant="flat"
            isLoading={isLoading === "google"}
            isDisabled={isLoading !== null}
            onPress={handleGoogleSignIn}
          >
            <img alt="" src={googleLogo} width={22} height={22} />
            Sign in with Google
          </Button>

          {!(isNativeDevice && platform === "android") && (
            <Button
              size="lg"
              fullWidth
              variant="flat"
              isLoading={isLoading === "apple"}
              isDisabled={isLoading !== null}
              onPress={handleAppleSignIn}
            >
              <img className="dark:invert" alt="" src={appleLogo} width={22} height={22} />
              Sign in with Apple
            </Button>
          )}
        </div>
      </div>
    </DrawerAndModal>
  );
}
